import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import RobitoLogo from "../components/AppLogo";
import { apiPostAuth } from "../utils/api";

const TITLE_MAX = 32;
const DESC_MAX = 128;
const SITE_ADDR_RE = /^[a-z0-9_]+$/; // lowercase letters, numbers, underscore
const IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"];

function TopBar() {
  const navigate = useNavigate();
  return (
    <header className="header">
      <div className="left">
        <button
          className="icon-btn"
          onClick={() => navigate("/dashboard", { replace: true })}
          aria-label="Back"
          title="Back"
          style={{ color: "white" }}
        >
          ‹
        </button>
      </div>
      <div className="logo">
        <RobitoLogo />
      </div>
      <div className="right" />
    </header>
  );
}

export default function Site() {
  const { site_id } = useParams();
  const navigate = useNavigate();

  // Loading
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // Site status
  const [siteValid, setSiteValid] = useState(0);
  const [siteAddressFull, setSiteAddressFull] = useState("");
  const [siteExpiryDate, setSiteExpiryDate] = useState("");
  const [isSiteExpired, setIsSiteExpired] = useState(false);

  // Section 1 — Site Address
  const [siteAddress, setSiteAddress] = useState("");
  const [initialSiteAddress, setInitialSiteAddress] = useState("");

  // Section 2 — Branding
  const [brandName, setBrandName] = useState("");
  const [pageTitle, setPageTitle] = useState("");
  const [pageDesc, setPageDesc] = useState("");
  const [initialBranding, setInitialBranding] = useState({
    brandName: "",
    pageTitle: "",
    pageDesc: "",
  });

  // Section 3 — Logo (required)
  const [logoUrl, setLogoUrl] = useState("");
  const [initialLogoUrl, setInitialLogoUrl] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [savingLogo, setSavingLogo] = useState(false);

  // Section 4 — Warna Latar (theme colors)
  const [primaryColor, setPrimaryColor] = useState("#2563eb");
  const [secondaryColor, setSecondaryColor] = useState("#f97316");
  const [initialColors, setInitialColors] = useState({
    primary: "#2563eb",
    secondary: "#f97316",
  });
  const [savingColors, setSavingColors] = useState(false);

  // Section 5 — CTA (required)
  const [ctaType, setCtaType] = useState("open_link");
  const [ctaMessage, setCtaMessage] = useState("");
  const [ctaButtonText, setCtaButtonText] = useState("");
  const [ctaTarget, setCtaTarget] = useState("");
  const [initialCta, setInitialCta] = useState({
    ctaType: "open_link",
    ctaMessage: "",
    ctaButtonText: "",
    ctaTarget: "",
  });
  const [savingCta, setSavingCta] = useState(false);

  // Section 6 — Contact (required)
  const [addr, setAddr] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [socials, setSocials] = useState({
    facebook: "",
    twitter: "",
    tiktok: "",
    instagram: "",
    youtube: "",
    linkedin: "",
  });
  const [initialContact, setInitialContact] = useState({
    addr: "",
    email: "",
    phone: "",
    socials: {
      facebook: "",
      twitter: "",
      tiktok: "",
      instagram: "",
      youtube: "",
      linkedin: "",
    },
  });
  const [savingContact, setSavingContact] = useState(false);

  // Section 7 — Highlight (optional)
  const [highlights, setHighlights] = useState([]);
  const [initialHighlights, setInitialHighlights] = useState([]);
  const [savingHighlights, setSavingHighlights] = useState(false);

  // Section 8 — Tabs (optional)
  const [tabs, setTabs] = useState([]);
  const [initialTabs, setInitialTabs] = useState([]);
  const [savingTabs, setSavingTabs] = useState(false);

  // Section 9 — Testimonial (optional)
  const [testimonials, setTestimonials] = useState([]);
  const [savingTestimonial, setSavingTestimonial] = useState(false);

  // Section 10 — Gallery (optional)
  const [galleryItems, setGalleryItems] = useState([]);
  const [savingGallery, setSavingGallery] = useState(false);

  // Save states for address & branding
  const [savingAddress, setSavingAddress] = useState(false);
  const [savingBranding, setSavingBranding] = useState(false);

  // Global message (toast)
  const [msg, setMsg] = useState("");
  const [errMsg, setErrMsg] = useState("");

  // ===== Helpers: site status from any API response =====
  const applySiteStatusFromResponse = (res) => {
    if (!res) return;
    const v =
      typeof res.site_valid !== "undefined"
        ? res.site_valid
        : typeof res.data?.site_valid !== "undefined"
        ? res.data.site_valid
        : undefined;
    const full =
      res.site_address_full ||
      (res.data && res.data.site_address_full) ||
      "";

    if (typeof v !== "undefined") setSiteValid(v);
    if (full) setSiteAddressFull(full);
    if (typeof v !== "undefined" && v !== 1 && !full) {
      setSiteAddressFull("");
    }
  };

  const normalizeGallery = (gal) => {
    const arr = Array.isArray(gal) ? gal : [];
    return arr.map((g, idx) =>
      typeof g === "string"
        ? { id: g, url: g, ratio: "1:1" }
        : {
            id: g.id ?? g.url ?? idx,
            url: g.url || "",
            ratio: g.ratio || "1:1",
          }
    );
  };

  const normalizeTestimonials = (raw) => {
    const arr = Array.isArray(raw) ? raw : [];
    return arr.map((t, idx) => {
      const picture =
        t.picture_url || t.picture || t.url || "";
      const id = t.id || picture || `t-${idx}`;
      return {
        id,
        picture,
        name: t.name || "",
        company: t.company || "",
        message: t.message || "",
      };
    });
  };

  // ===== Load existing site info =====
  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const res = await apiPostAuth("/site/get_site.php", {
          site_id: site_id,
        });

        if (res?.result === 1 && res?.data) {
          const d = res.data;
          const styles = res.styles;
          if (!mounted) return;

          applySiteStatusFromResponse(res);

          // Address
          const sa = d.site_address || "";
          setSiteAddress(sa);
          setInitialSiteAddress(sa);
          setSiteExpiryDate(res.expiry_date);
          setIsSiteExpired(res.is_site_expired === 1);

          // Branding
          const bn = d.brand_name || "";
          const pt = d.main_title || "";
          const pd = d.main_description || "";
          setBrandName(bn);
          setPageTitle(pt);
          setPageDesc(pd);
          setInitialBranding({
            brandName: bn,
            pageTitle: pt,
            pageDesc: pd,
          });

          // Logo
          const logo = d.logo_url || "";
          setLogoUrl(logo);
          setInitialLogoUrl(logo);

          // Colors (Warna Latar)
          const pColor = styles.primary_color;
          const sColor = styles.secondary_color;
          setPrimaryColor(pColor);
          setSecondaryColor(sColor);
          setInitialColors({
            primary: pColor,
            secondary: sColor,
          });

          // CTA
          const ctType = d.cta_type || "open_link";
          const ctMsg = d.cta_subtitle || "";
          const ctBtn = d.cta_button || "";
          const ctLink = d.cta_link || "";
          setCtaType(ctType);
          setCtaMessage(ctMsg);
          setCtaButtonText(ctBtn);
          setCtaTarget(ctLink);
          setInitialCta({
            ctaType: ctType,
            ctaMessage: ctMsg,
            ctaButtonText: ctBtn,
            ctaTarget: ctLink,
          });

          // Contact
          const cAddr = d.address || "";
          const cEmail = d.email || "";
          const cPhone = d.phone || "";
          const cSocials = {
            facebook: d.socials?.facebook || "",
            twitter: d.socials?.twitter || "",
            tiktok: d.socials?.tiktok || "",
            instagram: d.socials?.instagram || "",
            youtube: d.socials?.youtube || "",
            linkedin: d.socials?.linkedin || "",
          };
          setAddr(cAddr);
          setEmail(cEmail);
          setPhone(cPhone);
          setSocials(cSocials);
          setInitialContact({
            addr: cAddr,
            email: cEmail,
            phone: cPhone,
            socials: cSocials,
          });

          // Highlights
          const hl = Array.isArray(d.highlights) ? d.highlights : [];
          setHighlights(hl);
          setInitialHighlights(hl);

          // Tabs
          const tb = Array.isArray(d.tab_layout_items)
            ? d.tab_layout_items
            : [];
          setTabs(tb);
          setInitialTabs(tb);

          // Testimonials
          setTestimonials(
            normalizeTestimonials(d.testimonials)
          );

          // Gallery
          setGalleryItems(normalizeGallery(d.gallery));
        } else {
          setLoadError(
            "Gagal memuat data website."
          );
        }
      } catch (e) {
        setLoadError("Tidak dapat memuat data website.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [site_id]);

  // Snackbar auto-hide
  useEffect(() => {
    if (msg || errMsg) {
      const timer = setTimeout(() => {
        setMsg("");
        setErrMsg("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [msg, errMsg]);

  // ===== Validation helpers =====
  const addressValid =
    !!siteAddress && SITE_ADDR_RE.test(siteAddress || "");

  const pageTitleValid =
    (pageTitle || "").length > 0 &&
    (pageTitle || "").length <= TITLE_MAX;

  const pageDescValid =
    (pageDesc || "").length > 0 &&
    (pageDesc || "").length <= DESC_MAX;

  const brandingValid = pageTitleValid && pageDescValid;

  const logoValid = !!logoUrl || !!logoFile;

  const contactValid = !!(
    (addr && addr.trim()) ||
    (email && email.trim()) ||
    (phone && phone.trim())
  );

  const ctaTypeValid =
    ctaType === "open_link" ||
    ctaType === "contact_whatsapp" ||
    ctaType === "email";

  const ctaMessageValid = (ctaMessage || "").trim().length > 0;
  const ctaButtonTextValid = (ctaButtonText || "").trim().length > 0;

  const ctaTargetValid = (() => {
    const v = (ctaTarget || "").trim();
    if (!v) return false;
    if (ctaType === "email") return v.includes("@");
    if (ctaType === "contact_whatsapp") {
      const digits = v.replace(/[^\d]/g, "");
      return digits.length >= 8;
    }
    if (ctaType === "open_link") return v.length > 3;
    return false;
  })();

  const ctaValid =
    ctaTypeValid &&
    ctaMessageValid &&
    ctaButtonTextValid &&
    ctaTargetValid;

  // ===== Dirty checks =====
  const addressDirty = siteAddress !== initialSiteAddress;

  const brandingDirty =
    brandName !== initialBranding.brandName ||
    pageTitle !== initialBranding.pageTitle ||
    pageDesc !== initialBranding.pageDesc;

  const logoDirty =
    !!logoFile || logoUrl !== initialLogoUrl;

  const colorsDirty =
    primaryColor !== initialColors.primary ||
    secondaryColor !== initialColors.secondary;

  const socialsDirty = Object.keys(socials).some(
    (k) => socials[k] !== (initialContact.socials?.[k] || "")
  );

  const contactDirty =
    addr !== initialContact.addr ||
    email !== initialContact.email ||
    phone !== initialContact.phone ||
    socialsDirty;

  const ctaDirty =
    ctaType !== initialCta.ctaType ||
    ctaMessage !== initialCta.ctaMessage ||
    ctaButtonText !== initialCta.ctaButtonText ||
    ctaTarget !== initialCta.ctaTarget;

  const highlightsDirty =
    JSON.stringify(highlights) !==
    JSON.stringify(initialHighlights);

  const tabsDirty =
    JSON.stringify(tabs) !== JSON.stringify(initialTabs);

  // ===== Required sections summary =====
  const unfilledSections = [];
  if (!addressValid) unfilledSections.push("Alamat Website");
  if (!brandingValid) unfilledSections.push("Branding");
  if (!logoValid) unfilledSections.push("Logo");
  if (!ctaValid) unfilledSections.push("Call To Action (CTA)");
  if (!contactValid) unfilledSections.push("Kontak");

  // ===== Toast helpers =====
  const setToastError = (text) => {
    setErrMsg(text);
    setMsg("");
  };
  const setToastSuccess = (text) => {
    setMsg(text);
    setErrMsg("");
  };

  // ===== Save handlers (simple sections) =====
  const saveAddress = async () => {
    setMsg("");
    setErrMsg("");

    if (!addressValid) {
      setToastError(
        "Alamat Website wajib diisi dengan huruf kecil, angka, atau underscore."
      );
      return;
    }
    if (!addressDirty) {
      setToastError("Tidak ada perubahan pada Alamat Website.");
      return;
    }

    setSavingAddress(true);
    try {
      const res = await apiPostAuth(
        "/site/update_address.php",
        {
          site_id,
          site_address: siteAddress,
        }
      );

      applySiteStatusFromResponse(res);

      if (res?.result === 1) {
        setInitialSiteAddress(siteAddress);
        setToastSuccess("Alamat website tersimpan.");
      } else if (res?.address_used === 1) {
        setToastError("Alamat website telah digunakan.");
      } else {
        setToastError("Gagal menyimpan alamat website.");
      }
    } catch (e) {
      setToastError(
        "Tidak dapat menyimpan alamat saat ini."
      );
    } finally {
      setSavingAddress(false);
    }
  };

  const saveBranding = async () => {
    setMsg("");
    setErrMsg("");

    if (!brandingValid) {
      setToastError(
        `Judul Website & Deskripsi wajib diisi (Title ≤ ${TITLE_MAX}, Deskripsi ≤ ${DESC_MAX}).`
      );
      return;
    }
    if (!brandingDirty) {
      setToastError("Tidak ada perubahan pada Branding.");
      return;
    }

    setSavingBranding(true);
    try {
      const res = await apiPostAuth(
        "/site/update_branding.php",
        {
          site_id,
          brand_name: brandName,
          page_title: pageTitle,
          page_description: pageDesc,
        }
      );

      applySiteStatusFromResponse(res);

      if (res?.result === 1) {
        setInitialBranding({
          brandName,
          pageTitle,
          pageDesc,
        });
        setToastSuccess("Branding tersimpan.");
      } else if (res?.invalid_char === 1) {
        setToastError("Terdapat karakter yang tidak diizinkan.");
      } else {
        setToastError("Gagal menyimpan branding.");
      }
    } catch (e) {
      setToastError(
        "Tidak dapat menyimpan branding saat ini."
      );
    } finally {
      setSavingBranding(false);
    }
  };

  // Logo upload
  const onLogoFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!IMAGE_TYPES.includes(file.type)) {
      setToastError(
        "Logo harus berupa file JPG, JPEG, atau PNG."
      );
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setToastError("Ukuran file logo maksimal 5MB.");
      return;
    }

    setLogoFile(file);
    const previewUrl = URL.createObjectURL(file);
    setLogoUrl(previewUrl);
  };

  const saveLogo = async () => {
    setMsg("");
    setErrMsg("");

    if (!logoValid) {
      setToastError("Logo wajib diupload.");
      return;
    }
    if (!logoDirty) {
      setToastError("Tidak ada perubahan pada Logo.");
      return;
    }
    if (!logoFile) {
      setToastError(
        "Pilih file logo baru untuk disimpan."
      );
      return;
    }

    setSavingLogo(true);
    try {
      const formData = new FormData();
      formData.append("site_id", site_id);
      formData.append("logo", logoFile);

      const res = await apiPostAuth(
        "/site/update_logo.php",
        formData
      );

      applySiteStatusFromResponse(res);

      if (res?.result === 1 && res.logo_url) {
        setInitialLogoUrl(res.logo_url);
        setLogoUrl(res.logo_url);
        setLogoFile(null);
        setToastSuccess("Logo tersimpan.");
      } else if (res?.result === 1) {
        setInitialLogoUrl(logoUrl);
        setLogoFile(null);
        setToastSuccess("Logo tersimpan.");
      } else {
        setToastError("Gagal menyimpan logo.");
      }
    } catch (e) {
      setToastError(
        "Tidak dapat menyimpan logo saat ini."
      );
    } finally {
      setSavingLogo(false);
    }
  };

  // Warna Latar
  const saveColors = async () => {
    setMsg("");
    setErrMsg("");

    if (!colorsDirty) {
      setToastError("Tidak ada perubahan pada Warna Latar.");
      return;
    }

    setSavingColors(true);
    try {
      const res = await apiPostAuth("/site/update_colors.php", {
        site_id,
        primary_color: primaryColor,
        secondary_color: secondaryColor,
      });

      applySiteStatusFromResponse(res);

      if (res?.result === 1) {
        setInitialColors({
          primary: primaryColor,
          secondary: secondaryColor,
        });
        setToastSuccess("Warna latar tersimpan.");
      } else {
        setToastError(
          "Gagal menyimpan warna latar."
        );
      }
    } catch (e) {
      setToastError(
        "Tidak dapat menyimpan warna latar saat ini."
      );
    } finally {
      setSavingColors(false);
    }
  };

  const saveCta = async () => {
    setMsg("");
    setErrMsg("");

    if (!ctaValid) {
      setToastError(
        "CTA wajib diisi lengkap: tipe, pesan, teks tombol, dan tujuan."
      );
      return;
    }
    if (!ctaDirty) {
      setToastError("Tidak ada perubahan pada CTA.");
      return;
    }

    setSavingCta(true);
    try {
      const res = await apiPostAuth("/site/update_cta.php", {
        site_id,
        cta_type: ctaType,
        cta_message: ctaMessage,
        cta_button_text: ctaButtonText,
        cta_target: ctaTarget,
      });

      applySiteStatusFromResponse(res);

      if (res?.result === 1) {
        setInitialCta({
          ctaType,
          ctaMessage,
          ctaButtonText,
          ctaTarget,
        });
        setToastSuccess("CTA tersimpan.");
      } else {
        setToastError("Gagal menyimpan CTA.");
      }
    } catch (e) {
      setToastError(
        "Tidak dapat menyimpan CTA saat ini."
      );
    } finally {
      setSavingCta(false);
    }
  };

  const saveContact = async () => {
    setMsg("");
    setErrMsg("");

    if (!contactValid) {
      setToastError(
        "Isi minimal salah satu: alamat, email, atau nomor telepon."
      );
      return;
    }
    if (!contactDirty) {
      setToastError("Tidak ada perubahan pada Contact.");
      return;
    }

    setSavingContact(true);
    try {
      const res = await apiPostAuth(
        "/site/update_contact.php",
        {
          site_id,
          address: addr,
          email: email,
          phone: phone,
          facebook: socials.facebook,
          twitter: socials.twitter,
          tiktok: socials.tiktok,
          instagram: socials.instagram,
          youtube: socials.youtube,
          linkedin: socials.linkedin,
        }
      );

      applySiteStatusFromResponse(res);

      if (res?.result === 1) {
        setInitialContact({
          addr,
          email,
          phone,
          socials: { ...socials },
        });
        setToastSuccess("Kontak tersimpan.");
      } else {
        setToastError("Gagal menyimpan kontak.");
      }
    } catch (e) {
      setToastError(
        "Tidak dapat menyimpan kontak saat ini."
      );
    } finally {
      setSavingContact(false);
    }
  };

  // Highlights
  const addHighlight = () => {
    setHighlights([
      ...highlights,
      { title: "", description: "" },
    ]);
  };
  const updateHighlight = (index, field, value) => {
    const copy = [...highlights];
    copy[index] = { ...copy[index], [field]: value };
    setHighlights(copy);
  };
  const removeHighlight = (index) => {
    const copy = [...highlights];
    copy.splice(index, 1);
    setHighlights(copy);
  };
  const saveHighlights = async () => {
    setMsg("");
    setErrMsg("");

    if (!highlightsDirty) {
      setToastError(
        "Tidak ada perubahan pada Highlight."
      );
      return;
    }

    setSavingHighlights(true);
    try {
      const res = await apiPostAuth(
        "/site/update_highlights.php",
        {
          site_id,
          highlights: JSON.stringify(highlights),
        }
      );

      applySiteStatusFromResponse(res);

      if (res?.result === 1) {
        setInitialHighlights(highlights);
        setToastSuccess("Highlight tersimpan.");
      } else {
        setToastError("Gagal menyimpan highlight.");
      }
    } catch (e) {
      setToastError(
        "Tidak dapat menyimpan highlight saat ini."
      );
    } finally {
      setSavingHighlights(false);
    }
  };

  // Tabs
  const addTab = () => {
    setTabs([
      ...tabs,
      { tab_label: "", title: "", description: "" },
    ]);
  };
  const updateTab = (index, field, value) => {
    const copy = [...tabs];
    copy[index] = { ...copy[index], [field]: value };
    setTabs(copy);
  };
  const removeTab = (index) => {
    const copy = [...tabs];
    copy.splice(index, 1);
    setTabs(copy);
  };
  const saveTabs = async () => {
    setMsg("");
    setErrMsg("");

    if (!tabsDirty) {
      setToastError(
        "Tidak ada perubahan pada Tab Section."
      );
      return;
    }

    setSavingTabs(true);
    try {
      const res = await apiPostAuth("/site/update_tabs.php", {
        site_id,
        tabs: JSON.stringify(tabs),
      });

      applySiteStatusFromResponse(res);

      if (res?.result === 1) {
        setInitialTabs(tabs);
        setToastSuccess("Tabs tersimpan.");
      } else {
        setToastError("Gagal menyimpan tabs.");
      }
    } catch (e) {
      setToastError(
        "Tidak dapat menyimpan tabs saat ini."
      );
    } finally {
      setSavingTabs(false);
    }
  };

  // ===== Testimonials (add/remove only, per-op save) =====

  const addTestimonialDraft = () => {
    setTestimonials((prev) => [
      ...prev,
      {
        id: `new-${Date.now()}-${Math.random()}`,
        picture: "",
        pictureFile: null,
        name: "",
        message: "",
        company: "",
        isNew: true,
      },
    ]);
  };

  const onTestimonialPictureChange = (id, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!IMAGE_TYPES.includes(file.type)) {
      setToastError(
        "Testimonial picture harus JPG, JPEG, atau PNG."
      );
      return;
    }

    setTestimonials((prev) =>
      prev.map((item) =>
        item.id === id && item.isNew
          ? {
              ...item,
              pictureFile: file,
              picture: URL.createObjectURL(file),
            }
          : item
      )
    );
  };

  const updateNewTestimonialField = (
    id,
    field,
    value
  ) => {
    setTestimonials((prev) =>
      prev.map((item) =>
        item.id === id && item.isNew
          ? { ...item, [field]: value }
          : item
      )
    );
  };

  const saveNewTestimonial = async (id) => {
    setMsg("");
    setErrMsg("");

    const item = testimonials.find(
      (t) => t.id === id && t.isNew
    );
    if (!item) return;

    if (!item.pictureFile) {
      setToastError(
        "Gambar testimonial wajib diisi."
      );
      return;
    }
    if (!item.name.trim() || !item.message.trim()) {
      setToastError(
        "Nama dan pesan testimonial wajib diisi."
      );
      return;
    }

    if (savingTestimonial) return;

    setSavingTestimonial(true);
    try {
      const keep = testimonials
        .filter((t) => !t.isNew)
        .map((t) => ({
          id: t.id,
          picture: t.picture,
          name: t.name,
          company: t.company,
          message: t.message,
        }));

      const formData = new FormData();
      formData.append("site_id", site_id);
      formData.append("keep", JSON.stringify(keep));
      formData.append("picture", item.pictureFile);
      formData.append("name", item.name);
      formData.append("company", item.company);
      formData.append("message", item.message);

      const res = await apiPostAuth(
        "/site/add_testimonial.php",
        formData
      );

      applySiteStatusFromResponse(res);

      if (res?.result === 1 && res.testimonials) {
        setTestimonials(
          normalizeTestimonials(res.testimonials)
        );
        setToastSuccess("Testimonial ditambahkan.");
      } else {
        setToastError("Gagal menambah testimonial.");
      }
    } catch (e) {
      setToastError(
        "Tidak dapat menyimpan testimonial saat ini."
      );
    } finally {
      setSavingTestimonial(false);
    }
  };

  const removeTestimonial = async (id) => {
    setMsg("");
    setErrMsg("");

    const target = testimonials.find((t) => t.id === id);
    if (!target) return;

    // Hapus draft lokal tanpa API
    if (target.isNew) {
      setTestimonials((prev) =>
        prev.filter((t) => t.id !== id)
      );
      return;
    }

    if (savingTestimonial) return;

    setSavingTestimonial(true);
    try {
      const keep = testimonials
        .filter((t) => t.id !== id && !t.isNew)
        .map((t) => ({
          id: t.id,
          picture: t.picture,
          name: t.name,
          company: t.company,
          message: t.message,
        }));

      const res = await apiPostAuth(
        "/site/remove_testimonial.php",
        {
          site_id,
          id: id,
          keep: JSON.stringify(keep),
        }
      );

      applySiteStatusFromResponse(res);

      if (res?.result === 1 && res.testimonials) {
        setTestimonials(
          normalizeTestimonials(res.testimonials)
        );
        setToastSuccess("Testimonial dihapus.");
      } else {
        setToastError("Gagal menghapus testimonial.");
      }
    } catch (e) {
      setToastError(
        "Tidak dapat menghapus testimonial saat ini."
      );
    } finally {
      setSavingTestimonial(false);
    }
  };

  // ===== Gallery (single-file add, per-op save) =====

  const onGalleryFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!IMAGE_TYPES.includes(file.type)) {
      setToastError(
        "Gallery hanya menerima JPG, JPEG, atau PNG."
      );
      return;
    }

    if (savingGallery) return;

    setSavingGallery(true);
    try {
      const keep = galleryItems.map((item) => ({
        id: item.id,
        url: item.url,
        ratio: item.ratio || "1:1",
      }));

      const formData = new FormData();
      formData.append("site_id", site_id);
      formData.append("keep", JSON.stringify(keep));
      formData.append("image", file);
      formData.append("ratio", "1:1");

      const res = await apiPostAuth(
        "/site/add_gallery.php",
        formData
      );

      applySiteStatusFromResponse(res);

      if (res?.result === 1 && res.gallery) {
        setGalleryItems(
          normalizeGallery(res.gallery)
        );
        setToastSuccess("Gambar gallery ditambahkan.");
      } else {
        setToastError("Gagal menambah gambar.");
      }
    } catch (err) {
      setToastError(
        "Tidak dapat menyimpan gallery saat ini."
      );
    } finally {
      setSavingGallery(false);
      e.target.value = "";
    }
  };

  const setGalleryRatio = (id, ratio) => {
    setGalleryItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, ratio } : item
      )
    );
  };

  const removeGalleryItem = async (id) => {
    if (savingGallery) return;

    setMsg("");
    setErrMsg("");
    setSavingGallery(true);

    try {
      const keep = galleryItems
        .filter((i) => i.id !== id)
        .map((item) => ({
          id: item.id,
          url: item.url,
          ratio: item.ratio || "1:1",
        }));

      const formData = new FormData();
      formData.append("site_id", site_id);
      formData.append("id", id);
      formData.append("keep", JSON.stringify(keep));

      const res = await apiPostAuth(
        "/site/remove_gallery.php",
        formData
      );

      applySiteStatusFromResponse(res);

      if (res?.result === 1 && res.gallery) {
        setGalleryItems(
          normalizeGallery(res.gallery)
        );
        setToastSuccess("Gambar gallery dihapus.");
      } else {
        setToastError(
          "Gagal menghapus gambar gallery."
        );
      }
    } catch (err) {
      setToastError(
        "Tidak dapat menghapus gallery saat ini."
      );
    } finally {
      setSavingGallery(false);
    }
  };

  // ===== Render =====
  if (!site_id) {
    navigate("/dashboard", { replace: true });
    return null;
  }

  return (
    <div className="app-shell site-page">
      <TopBar />
      <main className="site-main">
        <h2 className="site-title">Site Setup</h2>
        {!isSiteExpired && (
          <p
          className="site-subtitle"
          dangerouslySetInnerHTML={{
            __html: siteValid && siteAddressFull
              ? `Yay website kamu sudah live di <b>${siteAddressFull}</b>. Jika isi website tidak berubah saat dibuka, mohon untuk refresh halaman website.`
              : "Lengkapi informasi berikut untuk mengaktifkan website kamu. Lengkapi semua informasi untuk tampilan terbaik."
          }}
        />
        )}

        {/* Required sections summary */}
        {isSiteExpired ? (
          <div className="site-message site-message-error show">
            Masa aktif website kamu telah habis, mohon untuk perpanjang website.
          </div>
        ) : !loading && !loadError && unfilledSections.length > 0 ? (
          <div className="site-message site-message-error show">
            Harap lengkapi bagian berikut agar website aktif:{" "}
            {unfilledSections.join(", ")}.
          </div>
        ) : null}

        {/* Open website / copy buttons */}
        {!loading &&
          !loadError &&
          siteValid === 1 &&
          siteAddressFull &&
          !isSiteExpired && (
            <div
              className="site-actions"
              style={{ marginBottom: 16 }}
            >
              <button
                className="site-btn"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(
                      siteAddressFull
                    );
                    alert(
                      "Tautan berhasil disalin ke clipboard!"
                    );
                  } catch (err) {
                    alert("Gagal menyalin tautan.");
                  }
                }}
              >
                <i
                  className="ti-clipboard"
                  style={{ marginRight: 8 }}
                ></i>
                Salin Tautan
              </button>
              <button
                className="site-btn"
                onClick={() =>
                  window.open(
                    siteAddressFull,
                    "_blank"
                  )
                }
              >
                <i
                  className="ti-link"
                  style={{ marginRight: 8 }}
                ></i>
                Buka Website
              </button>
            </div>
          )}

        <p
          className="site-subtitle"
          dangerouslySetInnerHTML={{
            __html: !isSiteExpired?`Website ini akan aktif sampai <b>${siteExpiryDate}</b>. Website akan dimatikan dan alamat website akan dikosongkan jika tidak diperpanjang.`
              :`Website telah tidak aktif sejak <b>${siteExpiryDate}</b>. Mohon untuk memperpanjang website untuk mengaktifkan kembali.`
          }}
        />

        <div
          className="site-actions"
          style={{ marginBottom: 16 }}
        >
          <button
            className="site-btn"
            onClick={() => navigate(`/dashboard/site/renew/${site_id}`)}
          >
            <i
              className="ti-stats-up"
              style={{ marginRight: 8 }}
            ></i>
            Perpanjang
          </button>
        </div>

        {loading ? (
          <div className="site-message">
            Memuat data…
          </div>
        ) : loadError ? (
          <div className="site-message site-message-error">
            {loadError}
          </div>
        ) : (
          <>
            {/* Snackbar / Toast */}
            <div
              className={
                "site-message " +
                (errMsg
                  ? "site-message-error"
                  : msg
                  ? "site-message-success"
                  : "") +
                (msg || errMsg ? " show" : "")
              }
            >
              {errMsg || msg}
            </div>

            {/* 1) Site Address */}
            <section className="site-card">
              <h3 className="site-section-title">
                1) Alamat Website
              </h3>
              <p className="site-hint">
                Gunakan huruf kecil, angka, atau
                underscore. Contoh: tokoku_123
              </p>

              <label className="site-label">
                Alamat Website
              </label>
              <input
                className="site-input"
                value={siteAddress}
                onChange={(e) =>
                  setSiteAddress(e.target.value)
                }
                placeholder="mis. tokoku_123"
                spellCheck={false}
                disabled={isSiteExpired}
              />
              {!addressValid && siteAddress && (
                <div className="site-error">
                  Hanya huruf kecil, angka, atau
                  underscore yang diperbolehkan.
                </div>
              )}

              <div className="site-actions">
                <button
                  className="site-btn"
                  onClick={saveAddress}
                  disabled={
                    isSiteExpired ||
                    savingAddress ||
                    !addressValid ||
                    !addressDirty
                  }
                >
                  {savingAddress
                    ? "Menyimpan…"
                    : "Simpan Alamat"}
                </button>
              </div>
            </section>

            {/* 2) Branding */}
            <section className="site-card">
              <h3 className="site-section-title">
                2) Branding
              </h3>

              <div className="site-field">
                <label className="site-label">
                  Nama Brand/Merek
                </label>
                <input
                  className="site-input"
                  value={brandName}
                  onChange={(e) =>
                    setBrandName(e.target.value)
                  }
                  placeholder="Nama brand/Merek"
                  disabled={isSiteExpired}
                />
              </div>

              <div className="site-field">
                <label className="site-label">
                  Judul Website{" "}
                  <span className="site-counter">
                    ({pageTitle.length}/{TITLE_MAX})
                  </span>
                </label>
                <input
                  className="site-input"
                  value={pageTitle}
                  onChange={(e) =>
                    setPageTitle(
                      e.target.value.slice(
                        0,
                        TITLE_MAX
                      )
                    )
                  }
                  placeholder="Judul Website (maks 32)"
                  disabled={isSiteExpired}
                />
              </div>

              <div className="site-field">
                <label className="site-label">
                  Deskripsi Website{" "}
                  <span className="site-counter">
                    ({pageDesc.length}/{DESC_MAX})
                  </span>
                </label>
                <textarea
                  className="site-input"
                  rows={3}
                  value={pageDesc}
                  onChange={(e) =>
                    setPageDesc(
                      e.target.value.slice(
                        0,
                        DESC_MAX
                      )
                    )
                  }
                  placeholder="Deskripsi Website (maks 128)"
                  disabled={isSiteExpired}
                />
              </div>

              <div className="site-actions">
                <button
                  className="site-btn"
                  onClick={saveBranding}
                  disabled={
                    savingBranding ||
                    !brandingValid ||
                    !brandingDirty
                  }
                >
                  {savingBranding
                    ? "Menyimpan…"
                    : "Simpan Branding"}
                </button>
              </div>
            </section>

            {/* 3) Logo (Required) */}
            <section className="site-card">
              <h3 className="site-section-title">
                3) Logo
              </h3>
              <p className="site-hint">
                Upload logo utama brand kamu (wajib).
                Format: JPG, JPEG, PNG (maks 5MB).
                Logo ideal adalah logo dengan background transparan(png).
              </p>

              {logoUrl && (
                <div className="site-field">
                  <label className="site-label">
                    Current Logo
                  </label>
                  <div className="logo-preview-wrap">
                    <img
                      src={logoUrl}
                      alt="Logo"
                      className="logo-preview"
                    />
                  </div>
                </div>
              )}

              <div className="site-field">
                <label className="site-label">
                  Upload Logo
                </label>
                <label className="site-file-label">
                  <span className="site-file-btn">
                    Pilih logo
                  </span>
                  <span className="site-file-text">
                    JPG, JPEG, PNG (max 5MB)
                  </span>
                  <input
                    className="site-file-input"
                    type="file"
                    accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                    onChange={onLogoFileChange}
                    disabled={isSiteExpired}
                  />
                </label>
              </div>

              {!logoValid && (
                <div className="site-error">
                  Logo wajib diisi untuk mengaktifkan
                  website.
                </div>
              )}

              <div className="site-actions">
                <button
                  className="site-btn"
                  onClick={saveLogo}
                  disabled={
                    savingLogo ||
                    !logoValid ||
                    !logoDirty
                  }
                >
                  {savingLogo
                    ? "Menyimpan…"
                    : "Simpan Logo"}
                </button>
              </div>
            </section>

            {/* 4) Warna Latar */}
            <section className="site-card">
              <h3 className="site-section-title">
                4) Warna Latar
              </h3>
              <p className="site-hint">
                Pilih warna tema untuk website kamu. Ini akan
                mempengaruhi watna tampilan tombol, elemen penting,
                dan latar.
              </p>

              <div className="site-field">
                <label className="site-label">
                  Warna Utama
                </label>
                <input
                  type="color"
                  className="site-input site-color-input"
                  value={primaryColor}
                  onChange={(e) =>
                    setPrimaryColor(e.target.value)
                  }
                  disabled={isSiteExpired}
                />
                <div className="site-hint">
                  Dipakai untuk warna utama latar dominan, tombol-tombol penting, dan teks utama.
                </div>
              </div>

              <div className="site-field">
                <label className="site-label">
                  Warna aksen
                </label>
                <input
                  type="color"
                  className="site-input site-color-input"
                  value={secondaryColor}
                  onChange={(e) =>
                    setSecondaryColor(
                      e.target.value
                    )
                  }
                  disabled={isSiteExpired}
                />
                <div className="site-hint">
                  Dipakai untuk variasi warna setelah Warna Utama. Warna aksen yang ideal adalah warna yang mendekati Warna Utama.
                </div>
              </div>

              <div className="site-actions">
                <button
                  className="site-btn"
                  onClick={saveColors}
                  disabled={
                    savingColors ||
                    !colorsDirty
                  }
                >
                  {savingColors
                    ? "Menyimpan…"
                    : "Simpan Warna Latar"}
                </button>
              </div>
            </section>

            {/* 5) CTA (Required) */}
            <section className="site-card">
              <h3 className="site-section-title">
                5) Call To Action (CTA)
              </h3>
              <p className="site-hint">
                Atur tombol utama untuk mengarahkan
                pengunjung melakukan aksi penting
                (WhatsApp, link, email). Wajib diisi.
              </p>

              <div className="site-field">
                <label className="site-label">
                  Jenis CTA
                </label>
                <select
                  className="site-input"
                  value={ctaType}
                  onChange={(e) => {
                    setCtaType(e.target.value);
                    setCtaTarget("");
                  }}
                  disabled={isSiteExpired}
                >
                  <option value="open_link">
                    Buka Link
                  </option>
                  <option value="contact_whatsapp">
                    Kontak via WhatsApp
                  </option>
                  <option value="email">
                    Kirim Email
                  </option>
                </select>
              </div>

              <div className="site-field">
                <label className="site-label">
                  Pesan CTA (teks promosi)
                </label>
                <textarea
                  className="site-input"
                  rows={3}
                  value={ctaMessage}
                  onChange={(e) =>
                    setCtaMessage(e.target.value)
                  }
                  placeholder="Contoh: Dapatkan penawaran spesial sekarang."
                  disabled={isSiteExpired}
                />
              </div>

              <div className="site-field">
                <label className="site-label">
                  Tombol CTA
                </label>
                <input
                  className="site-input"
                  value={ctaButtonText}
                  onChange={(e) =>
                    setCtaButtonText(
                      e.target.value
                    )
                  }
                  placeholder="Contoh: Hubungi Kami"
                  disabled={isSiteExpired}
                />
              </div>

              <div className="site-field">
                <label className="site-label">
                  {ctaType ===
                  "contact_whatsapp"
                    ? "WhatsApp Number"
                    : ctaType === "email"
                    ? "Email Tujuan"
                    : "Link Tujuan"}
                </label>
                <input
                  className="site-input"
                  value={ctaTarget}
                  onChange={(e) =>
                    setCtaTarget(
                      e.target.value
                    )
                  }
                  placeholder={
                    ctaType ===
                    "contact_whatsapp"
                      ? "08..."
                      : ctaType === "email"
                      ? "email@domain.com"
                      : "https://..."
                  }
                  disabled={isSiteExpired}
                />
              </div>

              {!ctaValid && (
                <div className="site-error">
                  CTA wajib diisi lengkap (tipe,
                  pesan, teks tombol, tujuan).
                </div>
              )}

              <div className="site-actions">
                <button
                  className="site-btn"
                  onClick={saveCta}
                  disabled={
                    savingCta ||
                    !ctaValid ||
                    !ctaDirty
                  }
                >
                  {savingCta
                    ? "Menyimpan…"
                    : "Simpan CTA"}
                </button>
              </div>
            </section>

            {/* 6) Contact (Required) */}
            <section className="site-card">
              <h3 className="site-section-title">
                6) Kontak
              </h3>
              <p className="site-hint">
                Isi minimal salah satu dari alamat,
                email, atau nomor telepon.
              </p>

              <div className="site-field">
                <label className="site-label">
                  Alamat
                </label>
                <input
                  className="site-input"
                  value={addr}
                  onChange={(e) =>
                    setAddr(e.target.value)
                  }
                  placeholder="Alamat"
                  disabled={isSiteExpired}
                />
              </div>

              <div className="site-field">
                <label className="site-label">
                  Email
                </label>
                <input
                  className="site-input"
                  type="email"
                  value={email}
                  onChange={(e) =>
                    setEmail(e.target.value)
                  }
                  placeholder="email@domain.com"
                  disabled={isSiteExpired}
                />
              </div>

              <div className="site-field">
                <label className="site-label">
                  Nomor Telepon
                </label>
                <input
                  className="site-input"
                  value={phone}
                  onChange={(e) =>
                    setPhone(e.target.value)
                  }
                  placeholder="+62..."
                  disabled={isSiteExpired}
                />
              </div>

              <div className="site-divider" />

              <h4 className="site-section-subtitle">
                Social Media (opsional)
              </h4>

              {[
                "facebook",
                "twitter",
                "tiktok",
                "instagram",
                "youtube",
                "linkedin",
              ].map((key) => (
                <div
                  key={key}
                  className="site-field"
                >
                  <label
                    className="site-label"
                    style={{
                      textTransform:
                        "capitalize",
                    }}
                  >
                    {key}
                  </label>
                  <input
                    className="site-input"
                    value={socials[key] || ""}
                    onChange={(e) =>
                      setSocials((prev) => ({
                        ...prev,
                        [key]:
                          e.target
                            .value,
                      }))
                    }
                    placeholder={`Link ${key} (opsional)`}
                    disabled={isSiteExpired}
                  />
                </div>
              ))}

              {!contactValid && (
                <div className="site-error">
                  Isi minimal salah satu dari
                  alamat, email, atau nomor
                  telepon.
                </div>
              )}

              <div className="site-actions">
                <button
                  className="site-btn"
                  onClick={saveContact}
                  disabled={
                    savingContact ||
                    !contactValid ||
                    !contactDirty
                  }
                >
                  {savingContact
                    ? "Menyimpan…"
                    : "Simpan Kontak"}
                </button>
              </div>
            </section>

            {/* 7) Highlight Section (Optional) */}
            <section className="site-card">
              <h3 className="site-section-title">
                7) Highlight (Opsional)
              </h3>
              <p className="site-hint">
                Tambahkan keunikan brand kamu.
                Setiap item berisi judul dan
                pesan singkat.
              </p>

              {highlights.map(
                (item, index) => (
                  <div
                    key={index}
                    className="site-subcard"
                  >
                    <div className="site-field">
                      <label className="site-label">
                        Judul
                      </label>
                      <input
                        className="site-input"
                        value={
                          item.title || ""
                        }
                        onChange={(e) =>
                          updateHighlight(
                            index,
                            "title",
                            e.target.value
                          )
                        }
                        placeholder="Contoh: Gratis Ongkir"
                        disabled={isSiteExpired}
                      />
                    </div>
                    <div className="site-field">
                      <label className="site-label">
                        Pesan
                      </label>
                      <textarea
                        className="site-input"
                        rows={2}
                        value={
                          item.description ||
                          ""
                        }
                        onChange={(e) =>
                          updateHighlight(
                            index,
                            "description",
                            e.target.value
                          )
                        }
                        placeholder="Contoh: Pengiriman gratis ke seluruh Indonesia."
                        disabled={isSiteExpired}
                      />
                    </div>
                    <button
                      type="button"
                      className="site-btn site-btn-ghost"
                      onClick={() =>
                        removeHighlight(
                          index
                        )
                      }
                      disabled={isSiteExpired}
                    >
                      Hapus Highlight
                    </button>
                  </div>
                )
              )}

              <div className="site-actions">
                <button
                  type="button"
                  className="site-btn site-btn-outline"
                  onClick={addHighlight}
                  disabled={isSiteExpired}
                >
                  + Tambah Highlight
                </button>
                <button
                  className="site-btn"
                  onClick={saveHighlights}
                  disabled={
                    savingHighlights ||
                    !highlightsDirty
                  }
                >
                  {savingHighlights
                    ? "Menyimpan…"
                    : "Simpan Highlight"}
                </button>
              </div>
            </section>

            {/* 8) Tab Section (Optional) */}
            <section className="site-card">
              <h3 className="site-section-title">
                8) Tabs (Opsional)
              </h3>
              <p className="site-hint">
                Buat beberapa tab berisi
                informasi penting brand kamu.
                Setiap tab punya nama, judul,
                dan pesan.
              </p>

              {tabs.map(
                (tab, index) => (
                  <div
                    key={index}
                    className="site-subcard"
                  >
                    <div className="site-field">
                      <label className="site-label">
                        Nama Tab
                      </label>
                      <input
                        className="site-input"
                        value={
                          tab.tab_label ||
                          ""
                        }
                        onChange={(e) =>
                          updateTab(
                            index,
                            "tab_label",
                            e.target.value
                          )
                        }
                        placeholder="Contoh: Tentang Kami"
                        disabled={isSiteExpired}
                      />
                    </div>
                    <div className="site-field">
                      <label className="site-label">
                        Judul Tab
                      </label>
                      <input
                        className="site-input"
                        value={
                          tab.title || ""
                        }
                        onChange={(e) =>
                          updateTab(
                            index,
                            "title",
                            e.target.value
                          )
                        }
                        placeholder="Judul di dalam tab"
                        disabled={isSiteExpired}
                      />
                    </div>
                    <div className="site-field">
                      <label className="site-label">
                        Pesan
                      </label>
                      <textarea
                        className="site-input"
                        rows={3}
                        value={
                          tab.description ||
                          ""
                        }
                        onChange={(e) =>
                          updateTab(
                            index,
                            "description",
                            e.target.value
                          )
                        }
                        placeholder="Isi konten unik untuk tab ini."
                        disabled={isSiteExpired}
                      />
                    </div>
                    <button
                      type="button"
                      className="site-btn site-btn-ghost"
                      onClick={() =>
                        removeTab(index)
                      }
                      disabled={isSiteExpired}
                    >
                      Hapus Tab
                    </button>
                  </div>
                )
              )}

              <div className="site-actions">
                <button
                  type="button"
                  className="site-btn site-btn-outline"
                  onClick={addTab}
                  disabled={isSiteExpired}
                >
                  + Tambah Tab
                </button>
                <button
                  className="site-btn"
                  onClick={saveTabs}
                  disabled={
                    savingTabs || !tabsDirty
                  }
                >
                  {savingTabs
                    ? "Menyimpan…"
                    : "Simpan Tabs"}
                </button>
              </div>
            </section>

            {/* 9) Testimonial Section (Optional) */}
            <section className="site-card">
              <h3 className="site-section-title">
                9) Testimonial (Opsional)
              </h3>
              <p className="site-hint">
                Tambahkan testimoni pelanggan.
                Setiap item berisi foto,
                nama, perusahaan (opsional),
                dan pesan.
              </p>

              {testimonials.map(
                (item) => (
                  <div
                    key={item.id}
                    className="site-subcard"
                  >
                    {item.isNew ? (
                      <>
                        <div className="site-field">
                          <label className="site-label">
                            Foto Testimonial
                          </label>
                          <label className="site-file-label">
                            <span className="site-file-btn">
                              Pilih gambar
                            </span>
                            <span className="site-file-text">
                              JPG, JPEG, PNG
                            </span>
                            <input
                              className="site-file-input"
                              type="file"
                              accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                              onChange={(
                                e
                              ) =>
                                onTestimonialPictureChange(
                                  item.id,
                                  e
                                )
                              }
                            />
                          </label>
                          {item.picture && (
                            <div className="logo-preview-wrap">
                              <img
                                src={
                                  item.picture
                                }
                                alt="Foto testimonial"
                                className="logo-preview"
                              />
                            </div>
                          )}
                        </div>
                        <div className="site-field">
                          <label className="site-label">
                            Nama
                          </label>
                          <input
                            className="site-input"
                            value={
                              item.name ||
                              ""
                            }
                            onChange={(e) =>
                              updateNewTestimonialField(
                                item.id,
                                "name",
                                e
                                  .target
                                  .value
                              )
                            }
                            placeholder="Nama pelanggan"
                          />
                        </div>
                        <div className="site-field">
                          <label className="site-label">
                            Perusahaan (opsional)
                          </label>
                          <input
                            className="site-input"
                            value={
                              item.company ||
                              ""
                            }
                            onChange={(e) =>
                              updateNewTestimonialField(
                                item.id,
                                "company",
                                e
                                  .target
                                  .value
                              )
                            }
                            placeholder="Nama perusahaan"
                          />
                        </div>
                        <div className="site-field">
                          <label className="site-label">
                            Pesan
                          </label>
                          <textarea
                            className="site-input"
                            rows={3}
                            value={
                              item.message ||
                              ""
                            }
                            onChange={(e) =>
                              updateNewTestimonialField(
                                item.id,
                                "message",
                                e
                                  .target
                                  .value
                              )
                            }
                            placeholder="Apa kata mereka tentang brand kamu?"
                          />
                        </div>
                        <div className="site-actions">
                          <button
                            type="button"
                            className="site-btn"
                            onClick={() =>
                              saveNewTestimonial(
                                item.id
                              )
                            }
                            disabled={
                              savingTestimonial
                            }
                          >
                            {savingTestimonial
                              ? "Menyimpan…"
                              : "Simpan Testimonial"}
                          </button>
                          <button
                            type="button"
                            className="site-btn site-btn-ghost"
                            onClick={() =>
                              removeTestimonial(
                                item.id
                              )
                            }
                            disabled={
                              savingTestimonial
                            }
                          >
                            Batalkan
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        {item.picture && (
                          <div className="site-field">
                            <label className="site-label">
                              Foto
                            </label>
                            <div className="logo-preview-wrap">
                              <img
                                src={
                                  item.picture
                                }
                                alt={
                                  item.name ||
                                  "Testimonial"
                                }
                                className="logo-preview"
                              />
                            </div>
                          </div>
                        )}
                        <div className="site-field">
                          <label className="site-label">
                            Nama
                          </label>
                          <div className="site-static">
                            {item.name ||
                              "-"}
                          </div>
                        </div>
                        {item.company && (
                          <div className="site-field">
                            <label className="site-label">
                              Perusahaan
                            </label>
                            <div className="site-static">
                              {
                                item.company
                              }
                            </div>
                          </div>
                        )}
                        <div className="site-field">
                          <label className="site-label">
                            Pesan
                          </label>
                          <div className="site-static">
                            {item.message ||
                              "-"}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="site-btn site-btn-ghost"
                          onClick={() =>
                            removeTestimonial(
                              item.id
                            )
                          }
                          disabled={
                            savingTestimonial || isSiteExpired
                          }
                        >
                          Hapus Testimonial
                        </button>
                      </>
                    )}
                  </div>
                )
              )}

              <div className="site-actions">
                <button
                  type="button"
                  className="site-btn site-btn-outline"
                  onClick={
                    addTestimonialDraft
                  }
                  disabled={
                    savingTestimonial || isSiteExpired
                  }
                >
                  + Tambah Testimonial
                </button>
              </div>
            </section>

            {/* 10) Gallery Section (Optional) */}
            <section className="site-card">
              <h3 className="site-section-title">
                10) Gallery (Opsional)
              </h3>
              <p className="site-hint">
                Upload foto-foto produk/toko
                kamu. Hanya JPG, JPEG, PNG.
                Hanya 1 file per upload.
                Perubahan disimpan saat
                tambah/hapus. Unggah minimal 3 foto untuk tampilan terbaik.
              </p>

              <div className="site-field">
                <label className="site-label">
                  Upload Image
                </label>
                <label className="site-file-label">
                  <span className="site-file-btn">
                    Pilih gambar
                  </span>
                  <span className="site-file-text">
                    1 file JPG/JPEG/PNG per
                    upload
                  </span>
                  <input
                    className="site-file-input"
                    type="file"
                    accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                    onChange={
                      onGalleryFileChange
                    }
                    disabled={
                      savingGallery
                    }
                  />
                </label>
              </div>

              <div className="gallery-grid">
                {galleryItems.map(
                  (item) => (
                    <div
                      key={item.id}
                      className="gallery-item"
                    >
                      <div className="gallery-thumb-wrap">
                        <img
                          src={item.url}
                          alt="Gallery"
                          className={"gallery-thumb ratio-3-4"}
                        />
                      </div>
                      <button
                        type="button"
                        className="site-btn site-btn-ghost"
                        style={{ marginTop: "10px" }}
                        onClick={() =>
                          removeGalleryItem(
                            item.id
                          )
                        }
                        disabled={
                          savingGallery || isSiteExpired
                        }
                      >
                        Hapus
                      </button>
                    </div>
                  )
                )}
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
